import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import validator from 'validator'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  AppBar,
  Backdrop,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { getMinInstitutionInfo } from 'src/redux/slices/tableSlice/institutionSlice'
import { requestAccount } from 'src/redux/apiCalls/authApiCall'

const RequestAccount = () => {
  const dispatch = useDispatch()
  const recaptchaRef = useRef(null)

  const { minInstitutionData, statusMin, error } = useSelector((state) => state.institution)

  const [email, setEmail] = useState('')
  const [connexion, setConnexion] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const langIsFr = minInstitutionData?.LANGUE_DEFAUT_ETAB === 'Français'

  const messages = useMemo(
    () => ({
      invalidEmail: langIsFr ? "Votre email n'est pas valide" : 'Your email is not valid',
      captchaRequired: langIsFr ? 'Veuillez valider le captcha' : 'Please complete the captcha',
      captchaExpired: langIsFr
        ? 'Le captcha a expiré, veuillez réessayer'
        : 'Captcha expired, please try again',
      pageTitle: langIsFr
        ? 'Portail des enseignants - Activer mon compte'
        : 'Teachers portal - Activate my account',
      email: langIsFr ? 'Email' : 'Mail',
      activate: langIsFr ? 'Activer' : 'Activate',
      auth: langIsFr ? "S'authentifier" : 'Authenticate',
      forgotPassword: langIsFr ? 'Mot de passe oublié ?' : 'Forgot your password ?',
      home: langIsFr ? 'Accueil' : 'Home',
      dbConnecting: langIsFr
        ? 'Connexion à la base de données en cours...'
        : 'Connection to the current database...',
      dbFailed: langIsFr
        ? 'Connexion impossible ! Veuillez réessayer...'
        : 'Connection impossible! please try again...',
    }),
    [langIsFr],
  )

  const [formValues, setFormValues] = useState({
    username: {
      error: false,
      errorMessage: messages.invalidEmail,
    },
  })

  const validateEmail = (value) => {
    const trimmed = value.trim()

    if (!trimmed || !validator.isEmail(trimmed)) {
      return {
        error: true,
        errorMessage: messages.invalidEmail,
      }
    }

    return {
      error: false,
      errorMessage: '',
    }
  }

  const handleInputChange = (event) => {
    const value = event.target.value
    setEmail(value)

    setFormValues((prev) => ({
      ...prev,
      username: validateEmail(value),
    }))
  }

  useEffect(() => {
    if (statusMin === 'idle') {
      dispatch(getMinInstitutionInfo())
    }

    setConnexion(statusMin === 'succeeded')
  }, [dispatch, statusMin])

  const institutionLabel = !connexion
    ? error
      ? messages.dbFailed
      : messages.dbConnecting
    : langIsFr
    ? minInstitutionData?.LIB_ETAB?.length > 90
      ? minInstitutionData?.LIB_ETAB_C
      : minInstitutionData?.LIB_ETAB
    : minInstitutionData?.LIB_ETAB_EN?.length > 90
    ? minInstitutionData?.LIB_ETAB_EN_C
    : minInstitutionData?.LIB_ETAB_EN

  const institutionLabelSmall = langIsFr
    ? minInstitutionData?.LIB_ETAB_C
    : minInstitutionData?.LIB_ETAB_EN_C

  const handleCaptchaExpired = () => {
    toast.warning(messages.captchaExpired)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isSubmitting) return

    const emailValidation = validateEmail(email)
    setFormValues((prev) => ({
      ...prev,
      username: emailValidation,
    }))

    if (emailValidation.error) return

    const captchaInstance = recaptchaRef.current
    const tokenCaptcha = captchaInstance?.getValue()

    if (!tokenCaptcha) {
      toast.error(messages.captchaRequired)
      return
    }

    try {
      setIsSubmitting(true)

      await dispatch(
        requestAccount({
          user: email.trim(),
          recaptchaToken: tokenCaptcha,
          lang: minInstitutionData?.LANGUE_DEFAUT_ETAB,
        }),
      )
    } catch (error) {
      toast.error(
        langIsFr ? "Erreur lors de la demande d'activation" : 'Account activation request failed',
      )
    } finally {
      if (captchaInstance) {
        captchaInstance.reset()
      }
      setIsSubmitting(false)
    }
  }

  const Copyright = () => (
    <Grid sx={{ p: 2 }}>
      <Typography variant="body2" color="textSecondary" align="center">
        {'Copyright © '}
        <Link
          href="https://universco.fcsdz.com/"
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
        >
          UNIVERSCO Web App 2024
        </Link>
      </Typography>
    </Grid>
  )

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!connexion || statusMin === 'idle' || statusMin === 'failed'}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ flexGrow: 1 }}>
        <AppBar sx={{ background: '#000000' }} position="static">
          <Toolbar>
            <CardMedia
              component="img"
              sx={{ width: 200, height: 60 }}
              image="/logo_universco.png"
              alt="Logo"
            />

            <Typography
              sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block', xl: 'none' }, ml: 2 }}
              variant="h5"
              component="div"
            >
              {institutionLabel}
            </Typography>

            <Typography
              sx={{ flexGrow: 1, display: { xs: 'block', lg: 'none', xl: 'block' }, ml: 2 }}
              variant="h5"
              component="div"
            >
              {institutionLabelSmall}
            </Typography>

            <Button
              color="inherit"
              component={RouterLink}
              to={window.location.origin.replace('teachers-', '')}
            >
              {messages.home}
            </Button>
          </Toolbar>
        </AppBar>
      </Box>

      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ m: 1 }}
      >
        <Grid item>
          <CardMedia
            component="img"
            sx={{ height: 80 }}
            image={`${process.env.REACT_APP_API_URL}/images/logo_institution.png`}
            alt="Logo"
          />
        </Grid>
      </Grid>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: '500px',
          margin: 'auto',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
          backgroundColor: 'white',
          marginTop: 2,
        }}
      >
        <Typography variant="h5" component="div" sx={{ mb: 2 }}>
          {messages.pageTitle}
        </Typography>

        <TextField
          fullWidth
          label={messages.email}
          margin="normal"
          variant="standard"
          onChange={handleInputChange}
          name="username"
          value={email}
          error={formValues.username.error}
          helperText={formValues.username.error ? formValues.username.errorMessage : ''}
        />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onExpired={handleCaptchaExpired}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          sx={{ mt: 2, background: '#000000' }}
          disabled={isSubmitting || !email.trim() || formValues.username.error}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : messages.activate}
        </Button>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" underline="hover">
            {messages.auth}
          </Link>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component={RouterLink} to="/forgot-password" underline="hover">
            {messages.forgotPassword}
          </Link>
        </Box>
      </Box>

      <Copyright />
    </>
  )
}

export default RequestAccount
